/**
 * MetricsWebFunnelsPage — 数据中台 · Web 行为漏斗看板（ADM-MTR-S016）
 *
 * 单个 WEB App 的七个有序分析 + 到账核对（dev-docs 06），一次看一个分析：
 * - 每个计数单位一张卡（段），段间不给比例；首行为汇总，可按该分析的维度闭集分组
 * - 第 2 级起只在成熟起点（起点日 + 6 ≤ 最近完整日）中计数，显示「到达率 = 计数 ÷ 适用起点」
 * - 未就绪按成因灰显：埋点接入中（PENDING_INSTRUMENTATION）/ 观察未满（PENDING_MATURITY），不显示 0
 * 数据走 useMetricsWebFunnels（GET /api/v1/metrics/web-funnels）；App 跟随顶栏 App 选择器，只接受 WEB App。
 * 布局沿用激活转化看板（筛选卡 + 视图页签 + 维度芯片 + 表格卡），无新视觉。
 */

import {
  WEB_FUNNEL_DIMENSIONS_BY_ANALYSIS,
  WEB_FUNNEL_FILTERS_BY_ANALYSIS,
  WEB_FUNNEL_MAX_CHAPTER_INDEX_LIMIT,
  WEB_FUNNEL_MAX_DAYS,
  WEB_FUNNEL_REQUIRED_DIMENSIONS,
  type WebFunnelAnalysis,
  type WebFunnelDimension,
  earliestPaywallConversionDay,
} from '@novel-admin/shared';
import { useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import type {
  WebFunnelCellView,
  WebFunnelMetricsView,
  WebFunnelSegmentView,
  WebFunnelViewData,
} from '~/data/adapters/metrics-web-funnels-adapter';
import { useApps, useMetricsWebFunnels } from '~/data/queries';
import { fmtCountry, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DateRangeButton } from '~/ui/DatePicker';
import { FilterButton } from '~/ui/FilterButton';
import { PageTabs } from '~/ui/PageTabs';
import { Select } from '~/ui/Select';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { ChipRow, ToggleChip, daysBetween, toDay } from './conversion-dashboard-shared';
import { MetricsDataExplanation } from './metrics-data-explanation';
import {
  Cal,
  EmptyBlock,
  ErrorBlock,
  OPEN_APP_SWITCHER_EVENT,
  PendTag,
  StateGate,
  type StateGateMode,
} from './metrics-shared';

const ANALYSES: { id: WebFunnelAnalysis; label: string; note: string }[] = [
  {
    id: 'new_visitor',
    label: '新访客转化',
    note: '首访 → 读到正文 → 碰到收费章 → 看到价格 → 发起支付 → 到账观察',
  },
  {
    id: 'ad_link',
    label: '投放链接',
    note: '打开段按打开次数、用户段按用户分开统计，段间不算比例',
  },
  {
    id: 'reading_depth',
    label: '按书阅读深度',
    note: '最大到达章号曲线（含跳章），标出收费起点，不是逐章读完率',
  },
  {
    id: 'locked_chapter',
    label: '收费章转化',
    note: '(用户, 章节) 到达后的看到价格、发起支付与结果拆分',
  },
  { id: 'price_payment', label: '价格与支付', note: '展示段按会话、支付段按 (用户, 商品)' },
  {
    id: 'a2hs_guide',
    label: '主屏引导',
    note: '看到引导 → 系统浏览器打开 → 从主屏打开 → 主屏回访，按第 1 级打开环境分组',
  },
  {
    id: 'activity_retention',
    label: '活跃与留存',
    note: '每日 web_page_view 活跃用户；新访客 D1 / D7 当天回访',
  },
  {
    id: 'settlement_check',
    label: '到账核对',
    note: '客户端观察到账单数与 Web Production 结算单数逐日比对',
  },
];

const DIMENSION_LABEL: Record<WebFunnelDimension, string> = {
  date: '起点日',
  source: '来源',
  ad_link: '投放链接',
  book: '书',
  chapter: '章',
  language: '语言',
  country: '国家',
  scenario: '入口',
  product: '商品',
  trigger: '触发方式',
  environment: '打开环境',
  device_platform: '设备平台',
};

const VALUE_LABEL: Partial<Record<WebFunnelDimension, Record<string, string>>> = {
  source: { NON_ORGANIC: '投放', ORGANIC: '自然' },
  scenario: { reader: '阅读器付费弹层', recharge: '充值页' },
  trigger: { MANUAL: '手动', CHAPTER: '章节', STAY: '停留', PAYMENT: '支付后' },
  environment: { HOME_SCREEN: '主屏', IN_APP: 'App 内置浏览器', BROWSER: '系统浏览器' },
  device_platform: { IOS: 'iOS', ANDROID: 'Android', DESKTOP: '桌面' },
};

const FAILURE_LABEL: Record<string, string> = {
  NOVEL_DELISTED: '书已下架',
  NOVEL_NOT_PURCHASED: '书未采购',
  NO_READABLE_CHAPTERS: '无可读章节',
  MALFORMED_CODE: '链接编码错误',
  BOOK_NOT_FOUND: '书不存在',
  APP_CONTENT_MISMATCH: '书不属于该 App',
};

const SOURCE_OPTIONS = [
  { value: 'NON_ORGANIC', label: '投放' },
  { value: 'ORGANIC', label: '自然' },
];

/** 起点日窗口默认取最近 7 个完整日（今天未走完，到昨天为止） */
function defaultStartRange(): [Date, Date] {
  // 日期控件与 toDay 按本地日历读写：用 UTC 日历分量构造本地日期，西半球时区也落在 UTC 昨天
  const today = new Date();
  const end = new Date(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1);
  const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 6);
  return [start, end];
}

/** 最近走完的 UTC 日（起点日上限） */
function utcYesterday(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1))
    .toISOString()
    .slice(0, 10);
}

function dimensionLabel(
  dimension: WebFunnelDimension,
  value: string | null | undefined,
  data: WebFunnelViewData,
) {
  if (value === null || value === undefined || value === '') return '未知';
  if (dimension === 'language') return fmtLang(value);
  if (dimension === 'country') return fmtCountry(value);
  if (dimension === 'book') return data.bookTitleById[value] || value;
  if (dimension === 'chapter') {
    const chapter = data.chapterById[value];
    if (!chapter) return value;
    const book = data.bookTitleById[chapter.novelId] ?? '';
    return `${book} 第 ${chapter.chapterIndex} 章`;
  }
  return VALUE_LABEL[dimension]?.[value] ?? value;
}

const pct = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(1)}%`);

function PendingCell({ status }: { status: NonNullable<WebFunnelCellView['pending']> }) {
  return status === 'PENDING_MATURITY' ? (
    <span className="chip chip-warn" title="PENDING_MATURITY · 起点日 + 6 天尚未走完，不计为流失">
      观察未满
    </span>
  ) : (
    <PendTag k="beh" label="埋点接入中" />
  );
}

function CellValue({ cell }: { cell: WebFunnelCellView }) {
  if (cell.pending) return <PendingCell status={cell.pending} />;
  return <>{cell.value === null ? '—' : cell.value.toLocaleString()}</>;
}

export default function MetricsWebFunnelsPage() {
  const appId = useAppScope((s) => s.appId);
  const webApps = useApps('WEB');
  const [analysis, setAnalysis] = useState<WebFunnelAnalysis>('new_visitor');
  const [range, setRange] = useState<[Date, Date]>(defaultStartRange);
  const [groupBy, setGroupBy] = useState<WebFunnelDimension[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [novelIds, setNovelIds] = useState<string[]>([]);
  const [adLinkIds, setAdLinkIds] = useState<string[]>([]);
  // 数字筛选分草稿与已提交两态：失焦或回车才提交，逐键输入不触发聚合请求
  const [chapterIndexInput, setChapterIndexInput] = useState('');
  const [maxChapterInput, setMaxChapterInput] = useState('');
  const [committedChapterInput, setCommittedChapterInput] = useState('');
  const [committedMaxChapterInput, setCommittedMaxChapterInput] = useState('');

  const webApp = webApps.data.find((app) => app.id === appId);
  const from = toDay(range[0]);
  const to = toDay(range[1]);
  const spanDays = daysBetween(from, to).length;
  const earliestDay = earliestPaywallConversionDay(new Date());
  const yesterday = utcYesterday();
  const rangeIssue =
    spanDays > WEB_FUNNEL_MAX_DAYS
      ? {
          title: `时间窗最多 ${WEB_FUNNEL_MAX_DAYS} 天`,
          note: `当前选择了 ${spanDays} 天，请缩短日期范围`,
        }
      : from < earliestDay
        ? {
            title: '起始日期超出埋点保留期',
            note: `埋点原始数据只保留 90 天，请从 ${earliestDay} 及之后开始选择`,
          }
        : to > yesterday
          ? { title: '结束日期尚未走完', note: `起点日最晚选到 ${yesterday}（UTC 昨天）` }
          : null;

  const allows = (param: keyof typeof WEB_FUNNEL_FILTERS_BY_ANALYSIS) =>
    (WEB_FUNNEL_FILTERS_BY_ANALYSIS[param] as readonly WebFunnelAnalysis[]).includes(analysis);
  // 空串 = 不传；非空须为闭区间内的正整数，否则报校验问题并停发请求（不静默当成不填）
  const parseBounded = (raw: string, max: number): number | null | 'invalid' => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    if (!/^[1-9]\d*$/.test(trimmed)) return 'invalid';
    const value = Number(trimmed);
    return value <= max ? value : 'invalid';
  };
  const parsedChapterIndex = allows('chapter_index')
    ? parseBounded(committedChapterInput, Number.MAX_SAFE_INTEGER)
    : null;
  const parsedMaxChapter = allows('max_chapter_index')
    ? parseBounded(committedMaxChapterInput, WEB_FUNNEL_MAX_CHAPTER_INDEX_LIMIT)
    : null;
  const chapterIndex = parsedChapterIndex === 'invalid' ? null : parsedChapterIndex;
  const maxChapterIndex = parsedMaxChapter === 'invalid' ? null : parsedMaxChapter;
  const inputIssue =
    parsedChapterIndex === 'invalid'
      ? { title: 'k 须为正整数', note: '修改后回车或移出输入框生效' }
      : parsedMaxChapter === 'invalid'
        ? {
            title: `曲线章数须为 1–${WEB_FUNNEL_MAX_CHAPTER_INDEX_LIMIT} 的整数`,
            note: '修改后回车或移出输入框生效',
          }
        : null;
  const blockingIssue = rangeIssue ?? inputIssue;

  const {
    data: queryData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useMetricsWebFunnels({
    appId: webApp?.id ?? '',
    analysis,
    from,
    to,
    groupBy,
    languages: allows('languages') ? languages : [],
    countries: allows('countries') ? countries : [],
    sources: allows('sources') ? sources : [],
    novelIds: allows('novel_ids') ? novelIds : [],
    adLinkIds: allows('ad_link_ids') ? adLinkIds : [],
    chapterIndex,
    maxChapterIndex,
    enabled: webApp !== undefined && blockingIssue === null,
  });

  const switchAnalysis = (next: WebFunnelAnalysis) => {
    setAnalysis(next);
    // 维度闭集按分析不同，切换后清掉对新分析不可用的分组
    setGroupBy((current) =>
      current.filter((d) => WEB_FUNNEL_DIMENSIONS_BY_ANALYSIS[next].includes(d)),
    );
  };
  const resetFilters = () => {
    setLanguages([]);
    setCountries([]);
    setSources([]);
    setNovelIds([]);
    setAdLinkIds([]);
    setChapterIndexInput('');
    setMaxChapterInput('');
    setCommittedChapterInput('');
    setCommittedMaxChapterInput('');
  };
  const activeFilterCount =
    (allows('languages') ? languages.length : 0) +
    (allows('countries') ? countries.length : 0) +
    (allows('sources') ? sources.length : 0) +
    (allows('novel_ids') ? novelIds.length : 0) +
    (allows('ad_link_ids') ? adLinkIds.length : 0) +
    (chapterIndex !== null ? 1 : 0) +
    (maxChapterIndex !== null ? 1 : 0);
  const withSelected = (options: string[], selected: string[]) => [
    ...new Set([...options, ...selected]),
  ];
  const current = ANALYSES.find((item) => item.id === analysis);
  const dimensionChoices = WEB_FUNNEL_DIMENSIONS_BY_ANALYSIS[analysis].filter(
    // 恒含的维度由服务端补上（主屏引导按打开环境、到账核对按日），芯片不提供取消
    (d) => WEB_FUNNEL_REQUIRED_DIMENSIONS[analysis] !== d,
  );

  // 占位数据（keepPreviousData）只在同一 App × 分析内复用：切换 App 或分析时旧结果不能挂在新页签下
  const data =
    queryData && queryData.app.id === webApp?.id && queryData.analysis === analysis
      ? queryData
      : undefined;
  const mode: StateGateMode =
    !webApp || blockingIssue
      ? 'normal'
      : isLoading || (!data && isFetching)
        ? 'loading'
        : isError || !data
          ? 'error'
          : 'normal';

  return (
    <div className="page" data-screen-label="Web 行为漏斗">
      <div className="page-header">
        <div>
          <h1 className="page-title">Web 行为漏斗</h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            只统计 WEB App 第一方埋点 · 有序漏斗 · 观察窗起点日起 7 个 UTC 日
            <Cal
              t="有序漏斗"
              title="后一级取不早于上一级证据的最早事件；不与原生 App 合并；真实付费与收入以结算事实为准（见「到账核对」）"
            />
          </div>
        </div>
      </div>

      {webApps.isError ? (
        // App 列表取不到时不能当成「没选 Web App」：给出错误态与重试
        <ErrorBlock note="GET /api/v1/apps 加载失败 · 请重试" onRetry={webApps.refetch} />
      ) : !webApp ? (
        <EmptyBlock
          title="请选择一个 Web App"
          note={
            webApps.isLoading
              ? '正在加载 App 列表…'
              : 'Web 行为漏斗逐 App 计算、只统计 WEB App，请在顶栏切换到一个 Web App'
          }
          action={
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => window.dispatchEvent(new Event(OPEN_APP_SWITCHER_EVENT))}
            >
              选择 App
            </button>
          }
        />
      ) : (
        <>
          <div
            className="card"
            style={{
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span className="hstack" style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}>
              <Icons.IconFilter size={13} />
              起点日
            </span>
            <DateRangeButton value={range} onChange={setRange} />
            {allows('sources') && (
              <FilterButton
                label="来源"
                options={SOURCE_OPTIONS}
                value={sources}
                onChange={setSources}
              />
            )}
            {allows('ad_link_ids') && (
              <FilterButton
                label="投放链接"
                searchable
                options={withSelected(
                  (data?.options.ad_links ?? []).map((link) => link.id),
                  adLinkIds,
                )}
                value={adLinkIds}
                onChange={setAdLinkIds}
              />
            )}
            {allows('novel_ids') && (
              <FilterButton
                label="书"
                searchable
                options={withSelected(
                  (data?.options.books ?? []).map((book) => book.id),
                  novelIds,
                ).map((id) => ({
                  value: id,
                  label: data?.bookTitleById[id] || id,
                }))}
                value={novelIds}
                onChange={setNovelIds}
              />
            )}
            {allows('languages') && (
              <FilterButton
                label="语言"
                options={withSelected(data?.options.languages ?? [], languages).map((v) => ({
                  value: v,
                  label: fmtLang(v),
                }))}
                value={languages}
                onChange={setLanguages}
              />
            )}
            {allows('countries') && (
              <FilterButton
                label="国家"
                searchable
                options={withSelected(data?.options.countries ?? [], countries).map((v) => ({
                  value: v,
                  label: fmtCountry(v),
                }))}
                value={countries}
                onChange={setCountries}
              />
            )}
            {allows('chapter_index') && (
              <label
                className="hstack"
                style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}
                title="用户段第 3 级「读到第 k 章」的 k；不填取该书收费起点"
              >
                k =
                <input
                  className="input mono"
                  inputMode="numeric"
                  placeholder="收费起点"
                  value={chapterIndexInput}
                  onChange={(e) => setChapterIndexInput(e.target.value.replace(/[^\d]/g, ''))}
                  onBlur={() => setCommittedChapterInput(chapterIndexInput)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setCommittedChapterInput(chapterIndexInput);
                  }}
                  style={{ width: 80 }}
                />
              </label>
            )}
            {allows('max_chapter_index') && (
              <label className="hstack" style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}>
                曲线到第
                <input
                  className="input mono"
                  inputMode="numeric"
                  placeholder="50"
                  value={maxChapterInput}
                  onChange={(e) => setMaxChapterInput(e.target.value.replace(/[^\d]/g, ''))}
                  onBlur={() => setCommittedMaxChapterInput(maxChapterInput)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setCommittedMaxChapterInput(maxChapterInput);
                  }}
                  style={{ width: 64 }}
                />
                章
              </label>
            )}
            {activeFilterCount > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters}>
                <Icons.IconX size={11} />
                重置 {activeFilterCount}
              </button>
            )}
            <div style={{ flex: 1 }} />
            {isFetching && !isLoading && (
              <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>更新中…</span>
            )}
          </div>

          <PageTabs
            label="分析"
            value={analysis}
            items={ANALYSES.map((item) => ({ id: item.id, label: item.label }))}
            onChange={switchAnalysis}
          />

          <div className="card vstack" style={{ padding: '12px 14px', gap: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{current?.note}</div>
            {dimensionChoices.length > 0 && (
              <ChipRow label="分组">
                {dimensionChoices.map((d) => (
                  <ToggleChip
                    key={d}
                    label={DIMENSION_LABEL[d]}
                    on={groupBy.includes(d)}
                    onClick={() =>
                      setGroupBy((selected) =>
                        selected.includes(d)
                          ? selected.filter((x) => x !== d)
                          : WEB_FUNNEL_DIMENSIONS_BY_ANALYSIS[analysis].filter(
                              (x) => x === d || selected.includes(x),
                            ),
                      )
                    }
                  />
                ))}
              </ChipRow>
            )}
          </div>

          <StateGate
            mode={mode}
            refreshing={isFetching}
            skeleton={<TableSkeleton rows={8} cols={7} />}
            errorNote="GET /api/v1/metrics/web-funnels 加载失败 · 请重试"
            onRetry={refetch}
          >
            {blockingIssue && <EmptyBlock title={blockingIssue.title} note={blockingIssue.note} />}
            {!blockingIssue && data && (
              <div className="vstack" style={{ gap: 12 }}>
                <CoverageNotice data={data} />
                {data.segments.map((segment) => (
                  <SegmentCard key={segment.key} data={data} segment={segment} />
                ))}
              </div>
            )}
          </StateGate>
        </>
      )}
    </div>
  );
}

function CoverageNotice({ data }: { data: WebFunnelViewData }) {
  const { coverageStart, observableStart, sessionReadyStart } = data.coverage;
  return (
    <div
      className="card-flat hstack"
      style={{ padding: '8px 12px', gap: 16, flexWrap: 'wrap', fontSize: 12 }}
    >
      <span>
        覆盖起点 <b className="mono">{coverageStart ?? '埋点接入中'}</b>
      </span>
      <span>
        可观测起点 <b className="mono">{observableStart ?? '—'}</b>
      </span>
      <span title="同一会话的级只认 session_id；会话就绪前这些级显示「埋点接入中」">
        会话就绪 <b className="mono">{sessionReadyStart ?? '埋点接入中'}</b>
      </span>
      <span>
        最近完整日 <b className="mono">{data.lastCompleteDay}</b>
      </span>
      {observableStart && data.window.from < observableStart && (
        <span style={{ color: 'var(--warn)' }}>早于可观测起点的起点日不计入</span>
      )}
    </div>
  );
}

function SegmentCard({
  data,
  segment,
}: { data: WebFunnelViewData; segment: WebFunnelSegmentView }) {
  const [detailKey, setDetailKey] = useState('summary');
  const summary = segment.summary;
  const hasExtras =
    summary !== null &&
    (summary.failures !== null ||
      summary.curve !== null ||
      summary.arrivals !== null ||
      summary.outcomes !== null);
  // 分组后每行都带自己的曲线 / 结果拆分：明细区按所选分组展示，缺省汇总
  const detailRow = segment.rows.find((row) => row.key === detailKey);
  const detailMetrics = detailRow ?? summary;
  const rowLabel = (row: (typeof segment.rows)[number]) =>
    data.groupBy.map((d) => dimensionLabel(d, row.dims[d], data)).join(' · ');
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div
        className="hstack"
        style={{
          padding: '10px 14px',
          gap: 8,
          borderBottom: '1px solid var(--border)',
          fontSize: 13,
        }}
      >
        <b>{segment.title}</b>
        <MetricsDataExplanation report="web-funnels" />
        <span className="chip">计数单位：{segment.unitLabel}</span>
        {segment.truncated && (
          <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
            分组行超过上限，仅展示前 {segment.rows.length.toLocaleString()} 行
          </span>
        )}
      </div>
      {segment.activeDays && <ActiveDaysTable days={segment.activeDays} />}
      {segment.settlementDays && <SettlementTable days={segment.settlementDays} />}
      {segment.summary && <MetricsTable data={data} segment={segment} summary={segment.summary} />}
      {hasExtras && segment.rows.length > 0 && (
        <div className="hstack" style={{ gap: 8, padding: '10px 14px 0', fontSize: 12 }}>
          <span style={{ color: 'var(--fg-muted)' }}>明细</span>
          <Select
            value={detailRow ? detailKey : 'summary'}
            onChange={(e) => setDetailKey(e.target.value)}
            style={{ width: 260 }}
          >
            <option value="summary">汇总</option>
            {segment.rows.map((row) => (
              <option key={row.key} value={row.key}>
                {rowLabel(row)}
              </option>
            ))}
          </Select>
        </div>
      )}
      {hasExtras && detailMetrics && (
        <SegmentExtras data={data} segment={segment} summary={detailMetrics} />
      )}
    </div>
  );
}

function MetricsTable({
  data,
  segment,
  summary,
}: {
  data: WebFunnelViewData;
  segment: WebFunnelSegmentView;
  summary: WebFunnelMetricsView;
}) {
  const rows: { key: string; label: string[]; metrics: WebFunnelMetricsView }[] = [
    {
      key: 'summary',
      label: data.groupBy.map((_, index) => (index === 0 ? '汇总' : '')),
      metrics: summary,
    },
    ...segment.rows.map((row) => ({
      key: row.key,
      label: data.groupBy.map((d) => dimensionLabel(d, row.dims[d], data)),
      metrics: row,
    })),
  ];
  const showMature = summary.matureStarts !== null && segment.unit !== 'OPEN';
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 640 }}>
        <thead>
          <tr>
            {data.groupBy.map((d) => (
              <th key={d} style={{ whiteSpace: 'nowrap' }}>
                {DIMENSION_LABEL[d]}
              </th>
            ))}
            <th className="num" title="第 1 级：全部起点（含观察未满）">
              起点
            </th>
            {showMature && (
              <th className="num" title="起点日 + 6 ≤ 最近完整日的起点；第 2 级起只在其中计数">
                成熟起点
              </th>
            )}
            {summary.stages.map((stage) => (
              <th
                key={stage.key}
                className="num"
                style={{ whiteSpace: 'nowrap' }}
                title="计数 · 到达率（÷ 适用起点）· 步骤转化率（÷ 上一级）"
              >
                {stage.label}
              </th>
            ))}
            {summary.paywallStartChapterIndex !== null || segment.key === 'reader_books' ? (
              <th className="num">收费起点</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={row.key}
              style={
                rowIndex === 0 ? { fontWeight: 600, background: 'var(--bg-subtle)' } : undefined
              }
            >
              {row.label.map((label, index) => (
                <td
                  key={data.groupBy[index]}
                  className={data.groupBy[index] === 'date' ? 'mono' : undefined}
                >
                  {label}
                </td>
              ))}
              <td className="num mono">
                <CellValue cell={row.metrics.starts} />
              </td>
              {showMature && <td className="num mono">{row.metrics.matureStarts ?? '—'}</td>}
              {row.metrics.stages.map((stage) => (
                <td key={stage.key} className="num mono" style={{ whiteSpace: 'nowrap' }}>
                  <CellValue cell={stage.count} />
                  {stage.count.pending === null && (
                    <span style={{ color: 'var(--fg-muted)' }}>
                      {' '}
                      ·{' '}
                      {stage.eligible === 0
                        ? // 起点为 0 时没有可判的分母，不是「不适用」
                          row.metrics.starts.value
                          ? '不适用'
                          : '—'
                        : pct(stage.reachRate)}
                      {stage.stepRate !== null && (
                        <span title="本级 ÷ 上一级（同一批起点）">
                          {' '}
                          · 步骤 {pct(stage.stepRate)}
                        </span>
                      )}
                    </span>
                  )}
                </td>
              ))}
              {summary.paywallStartChapterIndex !== null || segment.key === 'reader_books' ? (
                <td className="num mono">{row.metrics.paywallStartChapterIndex ?? '—'}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </Table>
      {segment.rows.length === 0 && data.groupBy.length > 0 && (
        <EmptyBlock title="当前筛选下没有分组数据" note="换个时间窗或放宽筛选" minHeight={140} />
      )}
    </div>
  );
}

function SegmentExtras({
  data,
  segment,
  summary,
}: {
  data: WebFunnelViewData;
  segment: WebFunnelSegmentView;
  summary: WebFunnelMetricsView;
}) {
  const failures =
    summary.failures && 'counts' in summary.failures ? Object.entries(summary.failures.counts) : [];
  const arrivals = summary.arrivals;
  const curve = summary.curve;
  const outcomes = summary.outcomes;
  return (
    <div className="vstack" style={{ gap: 10, padding: '10px 14px', fontSize: 12 }}>
      {segment.key === 'opens' && (
        <div className="hstack" style={{ gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--fg-muted)' }}>落地失败</span>
          {summary.failures && 'pending' in summary.failures ? (
            <PendingCell status={summary.failures.pending} />
          ) : failures.length === 0 ? (
            <span className="mono">0</span>
          ) : (
            failures.map(([reason, count]) => (
              <span key={reason}>
                {FAILURE_LABEL[reason] ?? reason} <b className="mono">{count}</b>
              </span>
            ))
          )}
        </div>
      )}
      {arrivals && 'pending' in arrivals && <PendingCell status={arrivals.pending} />}
      {arrivals && !('pending' in arrivals) && (
        <div className="hstack" style={{ gap: 16, flexWrap: 'wrap' }}>
          <span>
            锁定到达 <b className="mono">{arrivals.locked}</b>
          </span>
          <span title="进入收费章即自动解锁放行，中间级不适用">
            进入即放行 <b className="mono">{arrivals.autoUnlock}</b>
          </span>
        </div>
      )}
      {outcomes && 'pending' in outcomes && <PendingCell status={outcomes.pending} />}
      {outcomes && !('pending' in outcomes) && (
        <div style={{ overflowX: 'auto' }}>
          <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 480 }}>
            <thead>
              <tr>
                <th>结果（成熟起点）</th>
                <th className="num">付费后 · 阅读器</th>
                <th className="num">付费后 · 充值页</th>
                <th className="num">期间未付费</th>
              </tr>
            </thead>
            <tbody>
              {(['record', 'free_pass'] as const).map((kind) => (
                <tr key={kind}>
                  <td title={kind === 'record' ? '取得解锁记录，不等同本次扣费' : undefined}>
                    {kind === 'record' ? '取得解锁记录' : '免费放行'}
                  </td>
                  <td className="num mono">{outcomes[kind].paid_reader}</td>
                  <td className="num mono">{outcomes[kind].paid_recharge}</td>
                  <td className="num mono">{outcomes[kind].unpaid}</td>
                </tr>
              ))}
              <tr>
                <td>途径不明</td>
                <td className="num mono" colSpan={3}>
                  {outcomes.unknown_path}
                </td>
              </tr>
              <tr>
                <td>离开</td>
                <td className="num mono" colSpan={3}>
                  {outcomes.left}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      )}
      {curve && 'pending' in curve && <PendingCell status={curve.pending} />}
      {curve && !('pending' in curve) && (
        <div style={{ overflowX: 'auto' }}>
          {summary.paywallStartChapterIndex !== null && (
            <div style={{ marginBottom: 6 }}>
              收费起点 <b className="mono">第 {summary.paywallStartChapterIndex} 章</b>
            </div>
          )}
          <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 360 }}>
            <thead>
              <tr>
                <th className="num">到达第 k 章</th>
                <th className="num">(用户, 书) 数</th>
                <th className="num">到达率</th>
              </tr>
            </thead>
            <tbody>
              {curve.map((point) => (
                <tr key={point.chapterIndex}>
                  <td className="num mono">{point.chapterIndex}</td>
                  <td className="num mono">{point.count}</td>
                  <td className="num mono">{pct(point.rate)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div style={{ marginTop: 6, color: 'var(--fg-faint)' }}>
            分母为所选明细的成熟起点；按「书」分组后在明细里选书可看该书曲线与收费起点。从目录跳章时中间各章也计为到达。
          </div>
        </div>
      )}
      {segment.key === 'visitors' && (
        <div style={{ color: 'var(--fg-faint)' }}>
          只含完成首访归因上报的访客；到账观察是客户端观察到的到账，真实付费以「到账核对」与结算事实为准。
        </div>
      )}
      {data.analysis === 'locked_chapter' && (
        <div style={{ color: 'var(--fg-faint)' }}>
          付费与章节按「同一用户、到达之后、结果之前」的时间顺序对应；扣费与收入取结算事实。
        </div>
      )}
    </div>
  );
}

function ActiveDaysTable({ days }: { days: NonNullable<WebFunnelSegmentView['activeDays']> }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 320 }}>
        <thead>
          <tr>
            <th>日期</th>
            <th className="num">活跃用户（web_page_view）</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day.date}>
              <td className="mono">{day.date}</td>
              <td className="num mono">
                <CellValue cell={day.activeUsers} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function SettlementTable({ days }: { days: NonNullable<WebFunnelSegmentView['settlementDays']> }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 560 }}>
        <thead>
          <tr>
            <th>日期</th>
            <th className="num" title="web_purchase_settled，按 event_id 去重、client_ts 归日">
              观察到账单数
            </th>
            <th className="num" title="Web Production 结算，按 settled_at 归日">
              结算到账单数
            </th>
            <th className="num">漏报差额</th>
            <th className="num">漏报率</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day.date}>
              <td className="mono">{day.date}</td>
              <td className="num mono">
                <CellValue cell={day.observedSettled} />
              </td>
              <td className="num mono">{day.settled.toLocaleString()}</td>
              <td className="num mono">
                <CellValue cell={day.gap} />
              </td>
              <td className="num mono">
                {day.gapRatePct.pending ? (
                  <PendingCell status={day.gapRatePct.pending} />
                ) : day.gapRatePct.value === null ? (
                  '—'
                ) : (
                  `${day.gapRatePct.value.toFixed(1)}%`
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div style={{ padding: '6px 14px 10px', fontSize: 11.5, color: 'var(--fg-faint)' }}>
        差额含付款后未回到账观察点、换浏览器或清除站点数据的到账；只用于评估行为漏斗末级的低估程度。
      </div>
    </div>
  );
}
